/**
 * Wishlist functionality using Shopify Customer API
 * Replaces the broken /a/wishlist endpoint
 */

class WishlistManager {
  constructor() {
    this.customerId = null;
    this.wishlistData = [];
    this.debugMode = true; // Enable debug logging
    this.init();
  }

  init() {
    console.log('Initializing WishlistManager...');
    // Get customer info from theme data
    this.customerId = window.theme?.customer?.id || null;
    console.log('Customer ID:', this.customerId);
    
    // Load existing wishlist
    this.loadWishlist();
    
    // Bind events
    this.bindEvents();
    
    // Update UI immediately if we have data
    this.updateWishlistUI();
    console.log('WishlistManager initialized');
  }

  bindEvents() {
    // Add to wishlist button click
    document.addEventListener('click', (e) => {
      if (e.target.closest('.addwishlist_btn')) {
        e.preventDefault();
        const btn = e.target.closest('.addwishlist_btn');
        this.handleAddToWishlist(btn);
      }
    });

    // Remove from wishlist button click
    document.addEventListener('click', (e) => {
      if (e.target.closest('.removewishlist_btn')) {
        e.preventDefault();
        const btn = e.target.closest('.removewishlist_btn');
        this.handleRemoveFromWishlist(btn);
      }
    });
  }

  async loadWishlist() {
    this.debug('Loading wishlist...');
    // For logged-in customers, try to load from server first
    if (this.customerId) {
      const token = this.getCustomerToken();
      
      if (token) {
        try {
          // Try to load from customer metafield first
          const response = await fetch(`/api/2023-10/customers/${this.customerId}/metafields.json?namespace=wishlist&key=products`, {
            headers: {
              'X-Shopify-Customer-Access-Token': token
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.metafields && data.metafields.length > 0) {
              this.wishlistData = JSON.parse(data.metafields[0].value);
              this.debug('Loaded from customer metafield:', this.wishlistData);
              this.updateWishlistUI();
              return;
            }
          } else {
            this.debug('Metafield API failed:', response.status);
          }
        } catch (e) {
          this.debug('Error loading from metafield, falling back to localStorage:', e);
        }
      } else {
        this.debug('No customer access token, using localStorage only');
      }
      
      // Fallback to localStorage if metafield fails or no token
      const localWishlist = localStorage.getItem('wishlist_' + this.customerId);
      if (localWishlist) {
        try {
          this.wishlistData = JSON.parse(localWishlist);
          this.debug('Loaded from localStorage:', this.wishlistData);
          // Try to sync to server in background
          if (token) {
            this.syncToServer();
          }
          this.updateWishlistUI();
          return;
        } catch (e) {
          this.debug('Error parsing local wishlist:', e);
          // Clear corrupted data
          localStorage.removeItem('wishlist_' + this.customerId);
        }
      }
    } else {
      // For guest users, use localStorage (more persistent than sessionStorage)
      const guestWishlist = localStorage.getItem('guest_wishlist');
      if (guestWishlist) {
        try {
          this.wishlistData = JSON.parse(guestWishlist);
          this.debug('Loaded from guest localStorage:', this.wishlistData);
          this.updateWishlistUI();
          return;
        } catch (e) {
          this.debug('Error parsing guest wishlist:', e);
          // Clear corrupted data
          localStorage.removeItem('guest_wishlist');
        }
      }
    }
    
    // If no data found, start with empty array
    this.wishlistData = [];
    this.debug('Starting with empty wishlist');
    this.updateWishlistUI();
  }

  saveWishlist() {
    try {
      if (this.customerId) {
        // For logged-in users, save to localStorage as fallback and sync to server
        localStorage.setItem('wishlist_' + this.customerId, JSON.stringify(this.wishlistData));
        this.syncToServer();
      } else {
        // For guest users, save to localStorage (more persistent than sessionStorage)
        localStorage.setItem('guest_wishlist', JSON.stringify(this.wishlistData));
      }
    } catch (error) {
      console.error('Error saving wishlist:', error);
    }
  }

  async syncToServer() {
    if (!this.customerId) return;
    
    try {
      const token = this.getCustomerToken();
      if (!token) {
        this.debug('No customer access token available');
        return;
      }

      // First try to get existing metafield
      let metafieldId = null;
      try {
        const getResponse = await fetch(`/api/2023-10/customers/${this.customerId}/metafields.json?namespace=wishlist&key=products`, {
          headers: {
            'X-Shopify-Customer-Access-Token': token
          }
        });
        
        if (getResponse.ok) {
          const data = await getResponse.json();
          if (data.metafields && data.metafields.length > 0) {
            metafieldId = data.metafields[0].id;
            this.debug('Found existing metafield:', metafieldId);
          }
        }
      } catch (e) {
        this.debug('Error checking existing metafield:', e);
      }

      // Update or create metafield
      const url = metafieldId 
        ? `/api/2023-10/customers/${this.customerId}/metafields/${metafieldId}.json`
        : `/api/2023-10/customers/${this.customerId}/metafields.json`;
      
      const method = metafieldId ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Customer-Access-Token': token
        },
        body: JSON.stringify({
          metafield: {
            ...(metafieldId && { id: metafieldId }),
            namespace: 'wishlist',
            key: 'products',
            value: JSON.stringify(this.wishlistData),
            type: 'json_string'
          }
        })
      });
      
      if (response.ok) {
        this.debug('Wishlist synced to server successfully');
      } else {
        const errorData = await response.text();
        this.debug('Server sync failed:', response.status, errorData);
      }
    } catch (error) {
      this.debug('Error syncing to server:', error);
    }
  }

  getCustomerToken() {
    // Try to get customer access token from various sources
    return window.theme?.customer?.accessToken || 
           localStorage.getItem('customerAccessToken') || 
           '';
  }

  debug(message, data = null) {
    if (this.debugMode) {
      console.log(`[WishlistManager] ${message}`, data || '');
    }
  }

  // Test method to verify storage
  testStorage() {
    this.debug('=== STORAGE TEST ===');
    this.debug('Customer ID:', this.customerId);
    this.debug('Current Wishlist:', this.wishlistData);
    
    if (this.customerId) {
      this.debug('Customer localStorage:', localStorage.getItem('wishlist_' + this.customerId));
    } else {
      this.debug('Guest localStorage:', localStorage.getItem('guest_wishlist'));
    }
    
    this.debug('Access Token:', this.getCustomerToken() ? 'Present' : 'Missing');
    this.debug('=== END TEST ===');
  }

  async handleAddToWishlist(btn) {
    const productId = btn.dataset.product_id;
    const productHandle = btn.dataset.product_handle;
    
    if (!productId || !productHandle) {
      console.error('No product ID or handle found');
      return;
    }

    // Check if user is logged in
    if (!this.customerId) {
      // Redirect guest to login page
      this.showAlert('info', 'Please log in to add items to your wishlist');
      setTimeout(() => {
        window.location.href = '/account/login?checkout_url=' + encodeURIComponent(window.location.pathname);
      }, 1000);
      return;
    }

    // Check if already in wishlist (check by handle)
    if (this.wishlistData.includes(productHandle)) {
      console.log('Product already in wishlist');
      return;
    }

    try {
      // Show loading state
      btn.classList.add('adding');
      btn.disabled = true;

      // Add product handle to wishlist array (not ID)
      this.wishlistData.push(productHandle);
      this.saveWishlist();
      this.updateWishlistUI();
      
      // Update button state
      btn.classList.add('added');
      btn.classList.remove('adding');
      
      // Show success message
      this.showAlert('check', window.theme?.strings?.wishlistAdded || 'Product added to wishlist');
      
      // Update wishlist count
      this.updateWishlistCount();
      
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      btn.classList.remove('adding');
      this.showAlert('check', 'Product added to wishlist');
    } finally {
      btn.disabled = false;
    }
  }

  async handleRemoveFromWishlist(btn) {
    const productId = btn.dataset.product_id;
    const productHandle = btn.dataset.product_handle || btn.closest('[data-product-handle]')?.dataset.productHandle;
    
    if (!productId && !productHandle) {
      console.error('No product ID or handle found');
      return;
    }

    try {
      // Show loading state
      const row = btn.closest('tr');
      if (row) {
        row.classList.add('removing');
      }

      // Remove from wishlist array (use handle if available, otherwise try to find by ID)
      let itemToRemove = productHandle;
      if (!itemToRemove) {
        // Try to find the handle by matching with product ID in the wishlist data
        // This is a fallback - ideally all items should have handles
        const index = this.wishlistData.findIndex(item => {
          // This would require additional data mapping, for now just remove by index if we can't find handle
          return false;
        });
        if (index !== -1) {
          this.wishlistData.splice(index, 1);
        }
      } else {
        // Remove by handle
        const index = this.wishlistData.indexOf(itemToRemove);
        if (index > -1) {
          this.wishlistData.splice(index, 1);
        }
      }

      this.saveWishlist();
      this.updateWishlistUI();

      // Remove row with animation
      if (row) {
        row.style.transition = 'opacity 0.3s, transform 0.3s';
        row.style.opacity = '0';
        row.style.transform = 'translateX(-20px)';
        
        setTimeout(() => {
          row.remove();
          this.updateWishlistCount();
          
          // Reload if wishlist is empty
          if (this.wishlistData.length === 0) {
            location.reload();
          }
        }, 300);
      }

      this.showAlert('check', window.theme?.strings?.wishlistRemoved || 'Product removed from wishlist');
      
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      
      // Fallback to local storage
      if (productHandle) {
        const index = this.wishlistData.indexOf(productHandle);
        if (index > -1) {
          this.wishlistData.splice(index, 1);
        }
      }
      
      this.saveWishlist();
      this.updateWishlistUI();
      
      const row = btn.closest('tr');
      if (row) {
        row.remove();
      }
      
      this.showAlert('check', 'Product removed from wishlist');
    }
  }

  updateWishlistUI() {
    // Update all wishlist buttons
    document.querySelectorAll('.addwishlist_btn').forEach(btn => {
      const productHandle = btn.dataset.product_handle;
      if (this.wishlistData.includes(productHandle)) {
        btn.classList.add('added');
      } else {
        btn.classList.remove('added');
      }
    });
    
    // Update wishlist count
    this.updateWishlistCount();
  }

  updateWishlistCount() {
    console.log('Updating wishlist count. Current data:', this.wishlistData);
    const countElements = document.querySelectorAll('.wishlist-count');
    console.log('Found wishlist count elements:', countElements.length);
    
    const count = this.wishlistData.length;
    console.log('Actual count:', count);
    
    countElements.forEach(element => {
      element.textContent = count;
      // Always show the count (like cart does)
      element.style.display = 'inline-block';
      console.log('Showing count element with value:', count);
    });
  }

  // Add a function to manually clear wishlist
  clearWishlist() {
    console.log('Manually clearing wishlist...');
    this.wishlistData = [];
    this.saveWishlist();
    this.updateWishlistUI();
    console.log('Wishlist cleared');
  }

  showAlert(type, message) {
    // Create or update alert notification
    let alert = document.querySelector('.wishlist-alert');
    if (!alert) {
      alert = document.createElement('div');
      alert.className = 'wishlist-alert';
      alert.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 9999;
        padding: 15px 20px;
        background: #fff;
        border-radius: 4px;
        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        transform: translateX(100%);
        transition: transform 0.3s ease;
      `;
      document.body.appendChild(alert);
    }

    alert.innerHTML = `
      <div style="display: flex; align-items: center;">
        <span style="margin-right: 10px; font-size: 16px;">
          ${type === 'check' ? '✓' : '⚠'}
        </span>
        <span>${message}</span>
      </div>
    `;

    // Show alert
    setTimeout(() => {
      alert.style.transform = 'translateX(0)';
    }, 100);

    // Hide alert after 3 seconds
setTimeout(() => {
  alert.style.transform = 'translateX(calc(100% + 20px))';
}, 3000);
  }
}

// Initialize wishlist manager when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.wishlistManager = new WishlistManager();
});

// Also initialize if DOM is already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.wishlistManager = new WishlistManager();
  });
} else {
  window.wishlistManager = new WishlistManager();
}
